import { useDynamicEntity } from './useDynamicEntity';
import { useAuth } from './index';
import { usePricingMethods } from './usePricingMethods';

// Süreklilik enum → TR label sözlüğü (UI/table render için).
const RECURRENCE_LABEL_TR = {
  ONCE: 'Tek sefer',
  PER_JOB: 'İş süreci boyunca',
  PER_PROJECT: 'Proje süresi boyunca',
  DEFINED_MONTHS: 'Süre tanımlı',
};

// Statik ciro kanalı enum → TR label.
const REVENUE_CHANNEL_LABEL_TR = {
  YAZIM: 'Yazım',
  ONAY_SONRASI: 'Onay Sonrası',
  SATIS: 'Satış',
};

// Ücret kaynağı enum → TR label.
const FEE_SOURCE_LABEL_TR = {
  MONTHLY_UNIT_FEE: 'Aylık birim ücret',
  QUARTERLY_TOTAL_FEE: 'Çeyreğe isabet eden toplam ücret',
  NO_PREMIUM: 'Prim verilmez',
};

// İş durumu enum → TR label (chip render için).
const JOB_STATUS_LABEL_TR = {
  YAZIMDA: 'Yazımda',
  DEGERLENDIRMEDE: 'Değerlendirmede',
  ONAYLANDI: 'Onaylandı',
  REDDEDILDI: 'Reddedildi',
  KAPATILDI: 'Kapatıldı',
  TAMAMLANDI: 'Tamamlandı',
  DANISMANLIK_SONLANDIRILDI: 'Danışmanlık Sonlandırıldı',
  ORTAK_BULUNDU: 'Ortak Bulundu',
  ASAMA_BIR_BASLADI: 'Aşama 1 Başladı',
  ASAMA_BIR_BITTI: 'Aşama 1 Bitti',
  ASAMA_IKI_BASLADI: 'Aşama 2 Başladı',
  ASAMA_IKI_BITTI: 'Aşama 2 Bitti',
};

const useRevenueCalculationRules = () => {
  const { user } = useAuth();
  // Pricing method adlarını id → name çözmek için. DataTable render'ı
  // additionalData prop'u almıyor, bu yüzden zenginleştirmeyi hook'ta yapıyoruz.
  const { pricingMethods } = usePricingMethods();
  const pricingNameById = (Array.isArray(pricingMethods) ? pricingMethods : [])
    .reduce((acc, p) => { acc[p.id] = p.name; return acc; }, {});
  const dynamicHook = useDynamicEntity({
    entityType: 'revenueCalculationRules',
    gqlEntityKey: 'revenue_calculation_rules',
    queryName: 'GET_REVENUE_CALCULATION_RULES_QUERY',
    createMutation: 'CREATE_REVENUE_CALCULATION_RULE_MUTATION',
    updateMutation: 'UPDATE_REVENUE_CALCULATION_RULE_MUTATION',
    deleteMutation: 'DELETE_REVENUE_CALCULATION_RULE_MUTATION',
    updateVariableName: 'set',
    transformData: (rule) => {
      const validJobStatuses = Array.isArray(rule.valid_job_statuses)
        ? rule.valid_job_statuses
        : [];
      const pricingIds = Array.isArray(rule.pricing_method_ids)
        ? rule.pricing_method_ids
        : [];
      return {
        ...rule,
        company_name: rule.company?.name || `Company ${rule.company_id}`,
        jobTypeName: rule.job_type?.name || `#${rule.job_type_id}`,
        revenueChannelText:
          REVENUE_CHANNEL_LABEL_TR[rule.revenue_channel] || rule.revenue_channel || '-',
        pricingMethodIds: pricingIds,
        pricing_method_ids: pricingIds,
        // ID listesinden ad listesi — DataTable render imzasında
        // additionalData yok, bu yüzden ad çözümünü burada yapıyoruz.
        pricingMethodNames: pricingIds.map((id) => pricingNameById[id] || `#${id}`),
        recurrenceTypeText: RECURRENCE_LABEL_TR[rule.recurrence_type] || rule.recurrence_type,
        feeSourceText: FEE_SOURCE_LABEL_TR[rule.fee_source] || rule.fee_source || '-',
        validJobStatuses,
        validJobStatusesText: validJobStatuses
          .map((s) => JOB_STATUS_LABEL_TR[s] || s)
          .join(', '),
        status: rule.status ? 'active' : 'inactive',
        companyId: rule.company_id,
        jobTypeId: rule.job_type_id,
        statusGroup: rule.job_type?.status_group || 'standard',
      };
    },
    transformFormData: (formData) => {
      const recurrence = formData.recurrence_type || 'ONCE';
      const idsRaw = Array.isArray(formData.pricing_method_ids)
        ? formData.pricing_method_ids
        : (formData.pricing_method_ids ? [formData.pricing_method_ids] : []);
      const idsInt = idsRaw
        .map((v) => parseInt(v, 10))
        .filter((n) => Number.isInteger(n));
      const jobTypeIdParsed = formData.job_type_id
        ? parseInt(formData.job_type_id, 10)
        : null;
      const out = {
        // Boş bırakılabilir: satış cirosu kuralları iş türünden bağımsız.
        job_type_id: Number.isInteger(jobTypeIdParsed) ? jobTypeIdParsed : null,
        revenue_channel: formData.revenue_channel || null,
        pricing_method_ids: idsInt,
        recurrence_type: recurrence,
        defined_months: recurrence === 'DEFINED_MONTHS'
          ? (formData.defined_months ? parseInt(formData.defined_months) : null)
          : null,
        valid_job_statuses: Array.isArray(formData.valid_job_statuses)
          ? formData.valid_job_statuses
          : (formData.valid_job_statuses ? [formData.valid_job_statuses] : []),
        fee_source: formData.fee_source || null,
        status: formData.status === 'active' || formData.status === true,
        description: formData.description || null,
      };
      // company_id sadece CREATE'te. UPDATE'te Hasura update kolonlarında yok
      // (kayıt güncellenirken şirket değişmemeli).
      const isUpdate = !!formData.id;
      if (!isUpdate) {
        if (user?.role === 'ROOT' && formData.companyId) {
          out.company_id = parseInt(formData.companyId);
        } else {
          out.company_id = user?.companyId;
        }
      }
      return out;
    },
    customWhereCondition:
      user?.role === 'ROOT' ? {} : { company_id: { _eq: user?.companyId } },
  });

  return {
    ...dynamicHook,
    isRootUser: user?.role === 'ROOT',
  };
};

export { useRevenueCalculationRules };
